import { useState, useEffect, useCallback } from 'react';
import { Settings } from 'lucide-react';
import { useData } from '@/context/DataContext';
import { openRouter } from '@/services/openRouter';
import { wikiService, type SyncProgressCallback } from '@/services/wikiService';
import { useToast } from '@/components/ui/toast';
import { SyncBar } from './SyncBar';
import { WikiPageDrawer } from './WikiPageDrawer';
import type { WikiIndexEntry, WikiPage, WikiPageKind } from '@/types';

function groupByKind(pages: WikiIndexEntry[]): Record<WikiPageKind, WikiIndexEntry[]> {
  return {
    entity: pages.filter((p) => p.kind === 'entity'),
    concept: pages.filter((p) => p.kind === 'concept'),
    synthesis: pages.filter((p) => p.kind === 'synthesis'),
  };
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

const SECTION_LABELS: Record<WikiPageKind, string> = {
  entity: 'Entity Pages',
  concept: 'Concept Pages',
  synthesis: 'Syntheses',
};

export function KnowledgeTab() {
  const { allItems, isLoading: isItemsLoading } = useData();
  const { success, error: showError } = useToast();

  const [pages, setPages] = useState<WikiIndexEntry[]>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{
    current: number;
    total: number;
    itemTitle: string;
  } | null>(null);

  // Drawer state
  const [selectedPage, setSelectedPage] = useState<WikiPage | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const hasAiKey = openRouter.isConfigured();
  const hasItems = allItems.length > 0;

  // Load wiki index on mount
  useEffect(() => {
    let cancelled = false;
    async function loadIndex() {
      try {
        const index = await wikiService.getIndex();
        if (!cancelled) {
          setPages(index.pages);
          setLastSyncedAt(index.lastSyncedAt || null);
          setIsLoading(false);
        }
      } catch (err) {
        console.warn('Failed to load wiki index:', err);
        if (!cancelled) setIsLoading(false);
      }
    }
    void loadIndex();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSync = useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setSyncProgress(null);

    const onProgress: SyncProgressCallback = (current, total, itemTitle) => {
      setSyncProgress({ current, total, itemTitle });
    };

    try {
      const result = await wikiService.syncKnowledge(allItems, onProgress);
      const index = await wikiService.getIndex();
      setPages(index.pages);
      setLastSyncedAt(index.lastSyncedAt || null);
      success(`Synced! ${result.pagesCreated} new, ${result.pagesUpdated} updated.`);
    } catch (err) {
      console.error('Wiki sync failed:', err);
      showError('Sync failed. Check your AI key and try again.');
    } finally {
      setIsSyncing(false);
      setSyncProgress(null);
    }
  }, [allItems, isSyncing, success, showError]);

  const handlePageClick = useCallback(async (slug: string) => {
    const page = await wikiService.getPage(slug);
    if (page) {
      setSelectedPage(page);
      setDrawerOpen(true);
    }
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedPage(null);
  }, []);

  const handleBacklinkClick = useCallback((itemId: string) => {
    // Navigate to Items tab and let the user find the item
    // (Full item-opening integration would require passing openModal from Dashboard)
    setDrawerOpen(false);
    setSelectedPage(null);
    // Switch to items tab via URL
    window.history.replaceState(null, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
    console.info(`Backlink clicked: item ${itemId}`);
  }, []);

  const handleRelatedPageClick = useCallback(async (slug: string) => {
    const page = await wikiService.getPage(slug);
    if (page) {
      setSelectedPage(page);
    }
  }, []);

  // Keyboard nav between pages in drawer
  const allPageSlugs = pages.map((p) => p.slug);
  const currentIdx = selectedPage ? allPageSlugs.indexOf(selectedPage.slug) : -1;

  const handleDrawerNext = useCallback(() => {
    const nextSlug = allPageSlugs[currentIdx + 1];
    if (currentIdx >= 0 && nextSlug) {
      void handlePageClick(nextSlug);
    }
  }, [currentIdx, allPageSlugs, handlePageClick]);

  const handleDrawerPrev = useCallback(() => {
    const prevSlug = allPageSlugs[currentIdx - 1];
    if (currentIdx > 0 && prevSlug) {
      void handlePageClick(prevSlug);
    }
  }, [currentIdx, allPageSlugs, handlePageClick]);

  // Empty states — order matters:
  // 1. No AI key (doesn't need items data)
  // 2. Loading (items or wiki index still loading)
  // 3. No items
  if (!hasAiKey) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <Settings className="h-8 w-8 text-[var(--text-tertiary)] mb-4" />
        <p className="text-sm text-[var(--text-secondary)]">
          Knowledge requires AI. Add your OpenRouter key in{' '}
          <a href="/settings" className="underline">
            Settings
          </a>
          .
        </p>
      </div>
    );
  }

  if (isLoading || isItemsLoading) {
    return (
      <div className="space-y-4 py-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 bg-[var(--bg-secondary)] rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!hasItems) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <p className="text-sm text-[var(--text-secondary)]">
          Save some items first, then sync to build your knowledge base.
        </p>
      </div>
    );
  }

  const hasPages = pages.length > 0;
  const grouped = groupByKind(pages);

  return (
    <div className="space-y-4">
      {/* Sync bar */}
      <SyncBar
        lastSyncedAt={lastSyncedAt}
        pageCount={pages.length}
        isSyncing={isSyncing}
        syncProgress={syncProgress}
        onSync={handleSync}
        disabled={!hasAiKey}
      />

      {!hasPages ? (
        // Empty state: has items + AI key, no wiki yet
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <h3 className="font-serif text-xl font-semibold mb-2">Your knowledge, connected.</h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-md mb-6">
            You have {allItems.length} saved items across links, notes, and podcasts. Sync to build
            entity pages and concept maps that connect your research.
          </p>
        </div>
      ) : (
        // Populated state: show pages grouped by kind
        <div className="space-y-6">
          {(['entity', 'concept', 'synthesis'] as const).map((kind) => {
            const sectionPages = grouped[kind];
            if (sectionPages.length === 0) return null;

            return (
              <div key={kind}>
                <h3 className="font-serif text-lg font-semibold mb-2">{SECTION_LABELS[kind]}</h3>
                <div className="border-t border-[var(--border-primary)]">
                  <ul role="list">
                    {sectionPages.map((page) => (
                      <li key={page.slug}>
                        <button
                          onClick={() => void handlePageClick(page.slug)}
                          className="w-full flex items-center justify-between px-3 py-3 hover:bg-[var(--bg-secondary)] transition-colors text-left border-b border-[var(--border-primary)] last:border-b-0"
                          style={{ minHeight: '44px' }}
                        >
                          <div className="min-w-0 flex-1">
                            <span className="font-serif font-semibold text-sm">{page.title}</span>
                            <span className="ml-2 text-xs text-[var(--text-tertiary)]">
                              {page.itemCount} {page.itemCount === 1 ? 'item' : 'items'}
                            </span>
                          </div>
                          <span className="text-xs text-[var(--text-tertiary)] flex-shrink-0 ml-2">
                            {timeAgo(page.updatedAt)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Wiki page drawer */}
      {drawerOpen && selectedPage && (
        <WikiPageDrawer
          page={selectedPage}
          onClose={handleDrawerClose}
          onNext={currentIdx < allPageSlugs.length - 1 ? handleDrawerNext : undefined}
          onPrev={currentIdx > 0 ? handleDrawerPrev : undefined}
          onBacklinkClick={handleBacklinkClick}
          onRelatedPageClick={handleRelatedPageClick}
        />
      )}
    </div>
  );
}
